/**
 * What an annuity IS, and what terms this particular one is modelled on.
 *
 * OWNER-RAISED, 2026-09-01, on a Retirement Village strategy that committed $1,525,000 to a lifetime
 * annuity: "is this a 100% RCV annuity or a 0% annuity, who owns the annuity (Client 1 or client 2),
 * is it reversionary etc... this is critical."
 *
 * It was critical and it was invisible. The Suggestion Engine sets the residual capital value
 * explicitly (both variants are modelled and named), but says nothing about the rest — and the
 * engine's defaults are consequential:
 *
 *   OWNER          `rvAnnuityOwner || 'client'` — Applicant 1. Their age drives the threshold day,
 *                  the point at which the assets-test rate drops from 60% to 30%.
 *   REVERSIONARY   FALSE. For a couple that means the INCOME stops when the owner dies. Whether any
 *                  MONEY comes back is a different term — residual capital — and the two must be
 *                  said together or the sentence is false. See `deathSentence` below.
 *   INDEXATION     FALSE. The payment is flat for life, so its buying power falls every year.
 *
 * A default the client cannot see is a decision made on their behalf. This component states all four
 * terms wherever an annuity is proposed, and links to a plain-words explanation of what an annuity
 * is, because for most clients it is a foreign concept being met for the first time inside a
 * six- or seven-figure recommendation.
 *
 * Shared because the same gap exists wherever an annuity can be proposed — Aged Care, Support at
 * Home, the Downsizer, the Granny Flat and Cashflow & Age Pension all offer one.
 */
import React, { useEffect, useState } from 'react';
import { capitalReturnedWords } from './annuityWords.ts';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { formatCurrency } from '../../utils/index.ts';
import { Button, CurrencyInput } from '../ui/index.tsx';

export interface AnnuityTermsProps {
    /** What is paid for it. */
    purchaseAmount?: number | null;
    /** Residual capital value as a percentage of the purchase price (0 = nothing comes back). */
    residualPercent?: number | null;
    /** Whose annuity it is, by name. */
    ownerName?: string | null;
    /** The other member of the couple, where there is one — used to say who keeps (or loses) it. */
    partnerName?: string | null;
    /** Income continues to the surviving partner. */
    reversionary?: boolean;
    /** The payment rises with inflation. */
    indexed?: boolean;
    /** 'lifetime' or 'fixedTerm'. */
    annuityType?: string | null;
    /** What it pays each year, on the engine's indicative rate. */
    annualPayment?: number | null;
    /** Fixed term: how many years it pays for. */
    termYears?: number | null;
    /** Lifetime: years before the payments start (a deferred lifetime annuity). */
    deferralYears?: number | null;
    /** A module-supplied control for entering a real provider quote. */
    quote?: React.ReactNode;
    className?: string;
    /** The capital access schedule's period for this annuity, in years — the engines report it; null reads as the rule in general terms. */
    capitalAccessPeriodYears?: number | null;
    /** False when the shorter life table stood in for a missing sex, so the card can say so. */
    lifeExpectancySexKnown?: boolean | null;
}

/**
 * The terms this annuity is modelled on, stated rather than assumed.
 *
 * Every line is a fact the engine is already acting on. None of it is advice — it is the disclosure
 * that makes the figures above it readable.
 */
export function AnnuityTerms({
    purchaseAmount,
    residualPercent,
    ownerName,
    partnerName,
    reversionary = false,
    indexed = false,
    termYears = null,
    deferralYears = null,
    annuityType = 'lifetime',
    annualPayment = null,
    quote = null,
    className = '',
    capitalAccessPeriodYears = null,
    lifeExpectancySexKnown = null,
}: AnnuityTermsProps) {
    const [explain, setExplain] = useState(false);
    const [showDetail, setShowDetail] = useState(false);
    const rcv = Math.max(0, Math.min(100, Number(residualPercent) || 0));
    const isCouple = !!partnerName;
    const owner = ownerName || 'you';
    const amount = purchaseAmount ? formatCurrency(purchaseAmount) : 'the purchase price';
    const isLifetime = annuityType !== 'fixedTerm';
    const deferral = isLifetime ? Math.max(0, Math.round(Number(deferralYears) || 0)) : 0;
    const term = !isLifetime ? Math.max(0, Math.round(Number(termYears) || 0)) : 0;
    const forHowLong = isLifetime
        ? `${deferral > 0 ? `from year ${deferral + 1}, ` : ''}for as long as ${owner} lives`
        : `for ${term > 0 ? `${term} years` : 'a set number of years'}`;

    /*
     * WHAT COMES BACK, AND TO WHOM — the sentence the first version got wrong.
     *
     * OWNER-CAUGHT, 2026-09-01: "it reads as though as soon as the client dies, she gets nothing —
     * she ceases to receive the income but if its 100% capital back, she gets the lump sum."
     *
     * Exactly right, and the old wording was flatly false on a capital-returned annuity: it said the
     * survivor "receives nothing from it" while $500,000 was on its way back to the estate. The two
     * terms are not independent — REVERSIONARY decides whether the INCOME continues, RESIDUAL CAPITAL
     * decides whether the MONEY comes back — and stating either without the other misleads. They are
     * written as one sentence now, because that is the only way to say it truthfully.
     */
    const capitalBack = rcv >= 99.5 ? 'full' : rcv <= 0.5 ? 'none' : 'part';
    const stops = `When ${owner} dies the payments stop${isCouple ? `, and ${partnerName} does not continue receiving them` : ''}`;
    /*
     * WHAT COMES BACK FOLLOWS THE CAPITAL ACCESS SCHEDULE (25 Sep 2026). "Returned to the estate" full
     * stop was a promise no lifetime annuity makes: the full amount comes back only if death is within
     * the first half of life expectancy, then less, then nothing — the condition for the 60% treatment.
     * One shared sentence (annuityWords.ts) says so on every screen.
     */
    const backWords = isLifetime
        ? capitalReturnedWords({ residualPct: rcv, periodYears: capitalAccessPeriodYears, sexKnown: lifeExpectancySexKnown, ownerName: owner, amountWords: purchaseAmount ? `the ${formatCurrency(purchaseAmount)}` : null })
        : (capitalBack === 'full' ? `the ${amount} comes back at the end of the term` : capitalBack === 'none' ? `nothing is returned at the end of the term: the capital came back through the payments` : `${rcv}% of the ${amount} comes back at the end of the term`);
    const deathSentence =
        reversionary && isCouple
            ? `If ${owner} dies first, the payments continue to ${partnerName} for the rest of their life.`
            : capitalBack === 'none'
                ? `${stops}, and nothing is returned: the provider keeps the ${amount}.`
                : `${stops}. ${isLifetime ? backWords : `And ${backWords}.`}`;

    /*
     * ONE SENTENCE FIRST, the detail underneath (owner, 2026-09-01: "right now its daunting").
     *
     * Four labelled terms in a row is a specification, not an explanation — it asks a reader who has
     * never met the product to assemble the meaning themselves. The plain sentence carries the whole
     * answer; the terms stay one tap away for the reader who wants to check them.
     */
    return (
        <>
            <div className={`rounded-xl border border-slate-200 bg-white p-4 ${className}`}>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">About this annuity</p>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-700">
                    <span className="font-semibold text-slate-900">{amount}</span> goes into a{' '}
                    {isLifetime ? (deferral > 0 ? 'deferred lifetime' : 'lifetime') : 'fixed-term'} annuity in{' '}
                    <span className="font-semibold text-slate-900">{owner}&apos;s name</span>
                    {annualPayment ? (
                        <>
                            , paying about{' '}
                            <span className="font-semibold text-slate-900">{formatCurrency(annualPayment)} a year</span>
                            {' '}{forHowLong}
                        </>
                    ) : (
                        <>, paying an income {forHowLong}</>
                    )}
                    . {deathSentence}
                    {deferral > 0 && ` Nothing is paid for the first ${deferral} years; Centrelink counts 60% of the purchase price from the day it is bought and none of the income until the payments begin.`}
                    {!isLifetime && term > 0 && term <= 5 && ' A term of five years or less is treated like a term deposit: the value is deemed and the payments are not counted.'}
                </p>

                {/* Easy to miss, and it costs real money over a long projection. */}
                {!indexed && (
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                        The payment never changes — it does not rise with inflation, so it buys less each year.
                    </p>
                )}

                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] font-semibold">
                    <button
                        type="button"
                        onClick={() => setExplain(true)}
                        className="text-blue-600 underline decoration-blue-600/40 underline-offset-2 hover:text-blue-700"
                    >
                        What is an annuity, and what are the risks?
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowDetail((v) => !v)}
                        aria-expanded={showDetail}
                        className="text-slate-500 hover:text-slate-700"
                    >
                        {showDetail ? 'Hide the terms' : 'See the terms'}
                    </button>
                </div>

                {showDetail && (
                    <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 border-t border-slate-100 pt-3 text-[12px] leading-relaxed">
                        <dt className="font-semibold text-slate-700">Owner</dt>
                        <dd className="text-slate-600">
                            {owner}
                            {isCouple && ' — their age sets when the assets-test rate drops from 60% to 30%'}
                        </dd>
                        <dt className="font-semibold text-slate-700">Capital returned</dt>
                        <dd className="text-slate-600">
                            {capitalBack === 'none'
                                ? 'nil — the provider keeps the capital, which is why the income is higher'
                                : `${rcv}%${capitalBack === 'full' ? ', which is why the income is lower' : ''}. ${backWords}`}
                        </dd>
                        {isCouple && (
                            <>
                                <dt className="font-semibold text-slate-700">Reversionary</dt>
                                <dd className="text-slate-600">
                                    {reversionary
                                        ? `yes — the income continues to ${partnerName}`
                                        : `no — the income stops with ${owner}`}
                                </dd>
                            </>
                        )}
                        <dt className="font-semibold text-slate-700">Indexed</dt>
                        <dd className="text-slate-600">
                            {indexed ? 'yes — the payment rises each year' : 'no — the payment never changes'}
                        </dd>
                        {!isLifetime && (
                            <>
                                <dt className="font-semibold text-slate-700">Term</dt>
                                <dd className="text-slate-600">a fixed number of years, not for life</dd>
                            </>
                        )}
                    </dl>
                )}

                {/*
                  * A REAL QUOTE BEATS OUR RATE (owner, 2026-09-01: the Suggestion Engine "doesn't
                  * provide the ability to enter the actual quote"). The village module has had this
                  * since the annuity went in there; nowhere else did. Supplied by the module, because
                  * only the module knows how to re-rank the field on the new number.
                  */}
                {quote}
            </div>
            <AnnuityExplainerModal open={explain} onClose={() => setExplain(false)} />
        </>
    );
}

/**
 * The engine's own terms, rendered — the form every Suggestion Engine panel uses.
 *
 * Each engine now reports the terms it built the annuity on (`annuityTermsFor` in
 * meansTestStrategies.ts, and the equivalent in the village engine) rather than leaving each screen
 * to restate them. This component turns that record into names the client recognises, so the panels
 * cannot drift into describing the same product several different ways — the failure this codebase
 * keeps meeting, where two surfaces narrate one plan and disagree.
 *
 * Renders nothing when there is no annuity, so it can be dropped into a card unconditionally.
 */
export function EngineAnnuityTerms({
    terms,
    factFind,
    quote = null,
    className = '',
}: {
    terms?: Record<string, unknown> | null;
    factFind: any;
    quote?: React.ReactNode;
    className?: string;
}) {
    if (!terms || !(Number(terms.purchaseAmount) > 0)) return null;
    const clientName = factFind?.client?.firstName || 'you';
    const partnerName = factFind?.adviceFor === 'couple' ? (factFind?.partner?.firstName || 'your partner') : null;
    const ownerIsPartner = terms.owner === 'partner';
    return (
        <AnnuityTerms
            className={className}
            purchaseAmount={Number(terms.purchaseAmount) || 0}
            residualPercent={Number(terms.residualPercent) || 0}
            ownerName={ownerIsPartner ? (partnerName || 'your partner') : clientName}
            /* Whoever is NOT the owner is the one whose position depends on these terms. */
            partnerName={ownerIsPartner ? clientName : partnerName}
            reversionary={!!terms.reversionary}
            indexed={!!terms.indexed}
            annuityType={String(terms.annuityType || 'lifetime')}
            annualPayment={Number(terms.annualPayment) || null}
            termYears={Number(terms.termYears) || null}
            deferralYears={Number(terms.deferralYears) || null}
            capitalAccessPeriodYears={terms.capitalAccessPeriodYears != null ? Number(terms.capitalAccessPeriodYears) || null : null}
            lifeExpectancySexKnown={terms.lifeExpectancySexKnown == null ? null : !!terms.lifeExpectancySexKnown}
            quote={quote}
        />
    );
}

/**
 * "Enter the real quote" — the control every Suggestion Engine offers beside an annuity.
 *
 * OWNER-DIRECTED, 2026-09-01: the ability to edit the income belongs in every module, not two. The
 * rate is what the decision turns on — our indicative figure is a stand-in until a provider names a
 * number — and a quote can change WHICH strategy wins, so committing one re-ranks the field rather
 * than relabelling the winning card.
 *
 * Shared because it appeared three times before this, in three slightly different forms. The module
 * owns what "re-rank" means and passes the committed value in; this owns the words and the shape, so
 * the same offer reads identically wherever a client meets it.
 */
export function AnnuityQuoteControl({
    draft,
    onDraft,
    committed,
    onCommit,
    className = '',
}: {
    draft: number | string;
    onDraft: (next: number | string) => void;
    committed: number | null;
    /** Commit (or clear, with null) the quote. The module re-runs its own field on the change. */
    onCommit: (next: number | null) => void;
    className?: string;
}) {
    return (
        <div className={`mt-3 border-t border-slate-100 pt-3 ${className}`}>
            <p className="text-[12px] text-slate-600">
                Have a quote from a provider? Enter the yearly income they offered and every option is
                re-compared on your number instead of ours.
            </p>
            <div className="mt-2 flex flex-wrap items-end gap-2">
                <div className="w-44">
                    <CurrencyInput
                        label="Quoted income ($/yr)"
                        value={draft}
                        onChange={(v) => onDraft((v ?? '') as number | string)}
                    />
                </div>
                <Button
                    size="sm"
                    disabled={draft === '' || Number(draft) <= 0}
                    onClick={() => onCommit(Number(draft) || null)}
                >
                    Re-model on this quote
                </Button>
                {committed != null && (
                    <Button variant="secondary" size="sm" onClick={() => { onCommit(null); onDraft(''); }}>
                        Back to indicative
                    </Button>
                )}
            </div>
        </div>
    );
}

/**
 * Plain words on what an annuity is, how it works, and what can go wrong.
 *
 * Written for someone meeting the product for the first time. The risks are given the same weight as
 * the benefits, because a guaranteed income is genuinely attractive and the thing being given up —
 * access to a large sum of capital, permanently — is the part a client will not think of unaided.
 */
export function AnnuityExplainerModal({ open, onClose }: { open: boolean; onClose: () => void }) {
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = previous;
        };
    }, [open, onClose]);

    if (!open || typeof document === 'undefined') return null;

    return createPortal(
        /* z-70, because this opens from inside "Understanding your plan", which is itself a modal. */
        <div
            className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-brand-ink/55 p-4 sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="annuity-explainer-title"
            onClick={onClose}
        >
            <div
                className="my-4 w-full max-w-2xl rounded-2xl border border-slate-200/70 bg-white p-6 shadow-cardlg sm:p-7"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-4 flex items-start justify-between gap-4">
                    <div>
                        <h3 id="annuity-explainer-title" className="text-[23px] font-bold leading-tight">
                            <span className="bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text text-transparent">
                                What is an annuity?
                            </span>
                        </h3>
                        <p className="mt-0.5 text-sm text-slate-600">A guaranteed income, bought with a lump sum.</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="mt-1 shrink-0 rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    >
                        <X size={20} strokeWidth={2.4} />
                    </button>
                </div>

                <div className="space-y-3 text-[14.5px] leading-relaxed text-slate-700">
                    <p>
                        You hand a lump sum to an insurance company. In return they pay you a set amount every year —
                        for the rest of your life, or for a fixed number of years if you choose that instead. It is the
                        closest thing to a private pension you can buy.
                    </p>
                    <p>
                        <strong>Why it comes up in this tool.</strong> Centrelink counts a lifetime annuity differently
                        from money in the bank: only <strong>60% of what you paid</strong> is counted in the assets test
                        (for the first five years, or until age 85 if that is later, and 30% after that). Money in the bank is counted in full. So moving money into an annuity can
                        lift the Age Pension — sometimes substantially.
                    </p>

                    {/*
                      * THE TWO TERMS PEOPLE CONFLATE, separated explicitly. The owner read the first
                      * version of the card as saying the survivor got nothing at all, when the
                      * capital was on its way back. If the card can be misread that way, so can this.
                      */}
                    <div className="rounded-xl border border-slate-200 border-l-4 border-l-slate-400 bg-white p-4">
                        <p className="mb-1.5 font-bold text-slate-900">Two separate questions, often confused</p>
                        <ul className="list-disc space-y-1.5 pl-5 text-sm">
                            <li>
                                <strong>Does the income continue?</strong> That is what <em>reversionary</em> means. A
                                reversionary annuity keeps paying the surviving partner; a non-reversionary one stops.
                            </li>
                            <li>
                                <strong>Does the money come back?</strong> That is the <em>residual capital value</em>.
                                At nil the provider keeps it. At 100% the purchase price comes back to the estate if you die
                                within the first half of your life expectancy; after that the amount falls each year, and
                                nothing comes back beyond it. That is the most the rules allow while Centrelink still counts
                                only 60% of the annuity — a product promising the full amount for life would be counted in full.
                            </li>
                            <li>
                                They are independent. An annuity can stop paying on death and still return every dollar —
                                and one that keeps paying the survivor may leave nothing at the end.
                            </li>
                        </ul>
                    </div>

                    <div className="rounded-xl border border-slate-200 border-l-4 border-l-amber-500 bg-white p-4">
                        <p className="mb-1.5 font-bold text-slate-900">What you give up</p>
                        <ul className="list-disc space-y-1.5 pl-5 text-sm">
                            {/*
                              * THIS SAID "You cannot change your mind later and ask for the capital back" FLATLY.
                              * It is true of a nil-residual annuity and wrong of a capital-returned one, which
                              * carries a withdrawal value falling in a straight line over the schedule period.
                              * Stating it flatly overstated the risk of the shape that suits most clients better,
                              * and it contradicted the figure the choice cards now show.
                              */}
                            <li>
                                <strong>Most of the money stops being available.</strong> This is the big one, and how
                                far it goes depends on the shape you choose. With nothing coming back you cannot change
                                your mind at all: there is no cash-out value. With capital returned you can take some of
                                it back, but the most allowed falls each year to nothing, what the provider pays is
                                usually less than that maximum, and cashing out ends the income for good. Either way,
                                treat it as money you will not have for a large unexpected cost.
                            </li>
                            <li>
                                <strong>You may not get back what you paid.</strong> On a nil-residual annuity, if you
                                die early the provider keeps the balance and your estate receives nothing from it. Living
                                a long time is what makes it pay off; dying early is what makes it not.
                            </li>
                            <li>
                                <strong>Inflation, unless you pay for protection.</strong> A flat payment buys noticeably
                                less after fifteen or twenty years. An indexed annuity solves this but starts lower.
                            </li>
                            <li>
                                <strong>For a couple, whether it continues.</strong> If it is not{' '}
                                <em>reversionary</em>, the income stops when the owner dies. This is the term people most
                                often miss.
                            </li>
                            <li>
                                <strong>You are relying on the provider.</strong> Payments depend on the insurer still
                                being there in twenty or thirty years. They are regulated and required to hold capital
                                against exactly that, but it is not a government guarantee.
                            </li>
                        </ul>
                    </div>

                    <div className="rounded-xl border border-slate-200 border-l-4 border-l-emerald-600 bg-white p-4">
                        <p className="mb-1.5 font-bold text-slate-900">What you get</p>
                        <ul className="list-disc space-y-1.5 pl-5 text-sm">
                            <li><strong>Certainty.</strong> An income that arrives whatever markets do and however long you live.</li>
                            <li><strong>A better means-test outcome</strong> than the same money sitting in the bank.</li>
                            <li><strong>No decisions to make.</strong> Nothing to manage, rebalance or worry about.</li>
                        </ul>
                    </div>

                    {/*
                      * HOW YOU WOULD ACTUALLY GET ONE. Left unsaid, the whole explainer describes a
                      * product with no route to it — which is its own kind of dead end.
                      */}
                    <p className="text-sm text-slate-600">
                        <strong>How you would actually buy one.</strong> A handful of insurers sell them. You, or an
                        adviser, ask for a quote on the amount, the owner and the options above; the rate they come back
                        with is what decides whether this is worth doing. The figures here use an indicative rate — enter
                        a real quote where the tool offers and every option is re-modelled on your number, not ours.
                    </p>
                </div>
            </div>
        </div>,
        document.body,
    );
}

export default AnnuityTerms;
